import Web3 from "web3";
import { ethers } from 'ethers';

import Onboard from '@web3-onboard/core';
import coinbaseWalletModule from '@web3-onboard/coinbase';
import injectedModule from '@web3-onboard/injected-wallets';
import trezorModule from '@web3-onboard/trezor';
import ledgerModule from '@web3-onboard/ledger';
import gnosisModule from '@web3-onboard/gnosis';
import fortmaticModule from '@web3-onboard/fortmatic';
import walletConnectModule from '@web3-onboard/walletconnect';
import portisModule from '@web3-onboard/portis';
import torusModule from '@web3-onboard/torus';

import Notify from "bnc-notify"

import config from '@/constants/config'
import coins from '@/constants/coins'
import coinsEthereum from '@/constants/coins/coins-ethereum'
import { getNowTimestamp } from '@/utils/Date'
import * as common from './utils/common.js'
import * as state from './contract.js'
import { infura_url, multicall_address, multicall_abi } from '@/constants/abis'

const injected = injectedModule();
const trezor = trezorModule({
    email: 'info@curve.finance',
    appUrl: 'https://classic.curve.finance',
    rpcUrl: config.rpcUrl,
});
const ledger = ledgerModule();
const gnosis = gnosisModule();
const coinbaseWalletSdk = coinbaseWalletModule({ darkMode: true });
const fortmatic = fortmaticModule({ apiKey: 'pk_live_190B10CE18F47DCD' });
const walletConnect = walletConnectModule();
const portis = portisModule({ apiKey: 'a3bb2525-5101-4a9c-b300-febc6319c3b4' });
const torus = torusModule();

export const notify = Notify({
    dappId: 'c68d8ec3-9b9a-4ba5-a3eb-6232eff79030',
    networkId: config.networkId,
    desktopPosition: 'topRight',
})

export function notifyHandler(hash) {
    let { emitter } = notify.hash(hash)
    emitter.on('all', transaction => ({
        onclick: () => window.open(`${config.explorerBaseUrl.tx}${transaction.hash}`, '_blank', 'noopener, norefferer')
    })
    )
}

export function notifyNotification(message, type = 'pending') {
    let notificationObject = {
        eventCode: 'notification',
        type: type,
        message: message,
    }

    return notify.notification(notificationObject)
}

let isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

export const onboard = config.blocknativeSupport === true ?
    Onboard({
        wallets: [
            injected,
            trezor,
            ledger,
            gnosis,
            coinbaseWalletSdk,
            fortmatic,
            walletConnect,
            portis,
            torus,
        ],
        chains: [{
            id: `0x${(config.networkId).toString(16)}`,
            rpcUrl: (config.blocknativeUnlockedRpcUrl || config.rpcUrl),
            token: coins[config.nativeAssetId].symbol,
            label: config.name,
        }, ...(
            // Always attach Ethereum config because some calls are made to it all the time
            config.blockchainId !== 'ethereum' ? [{
                id: `0x${(config.allConfigs.ethereum.networkId).toString(16)}`,
                rpcUrl: config.allConfigs.ethereum.blocknativeUnlockedRpcUrl,
                token: coinsEthereum[config.allConfigs.ethereum.nativeAssetId].symbol,
                label: config.allConfigs.ethereum.name,
            }] : []
        )],
        appMetadata: {
            name: 'Curve',
            description: 'Efficient stablecoin and non-stablecoin swapping',
            icon: 'https://classic.curve.finance/logo-square.svg',
        },
        accountCenter: {
            desktop: {
                enabled: false,
            },
        },
    }) :
    null;

if (config.blocknativeSupport === true) {
    const walletsSub = onboard.state.select('wallets');
    walletsSub.subscribe((wallets) => {
        const lastConnectedWallet = wallets[0];

        if (typeof lastConnectedWallet !== 'undefined') {
            // Keep track of active wallet to try and auto-connect back to it on next page load
            localStorage.setItem('selectedWallet', lastConnectedWallet.label);

            // Update app state
            state.contract.web3 = new Web3(lastConnectedWallet.provider)
            state.contract.theProvider = new Web3(lastConnectedWallet.provider)
            state.contract.walletName = lastConnectedWallet.label;
            state.contract.walletProvider = lastConnectedWallet.provider;
            state.contract.ethers = new ethers.providers.Web3Provider(lastConnectedWallet.provider);
            state.contract.default_account = lastConnectedWallet.accounts[0]?.address;

            // Update network-related app state
            if (parseInt(lastConnectedWallet.chains?.[0]?.id, 16) !== config.networkId) {
                state.contract.error = `Error: wrong network type. Please switch to ${config.name}`;
                state.contract.networkIdError = `Error: wrong network type. Please switch to ${config.name}`;
                state.contract.showShares = false;
            } else {
                state.contract.error = '';
                state.contract.networkIdError = '';
                state.contract.showShares = true;
            }
        } else {
            state.contract.web3 = new Web3(infura_url);
            state.contract.default_account = '0x0000000000000000000000000000000000000000';
        }

        // Historical stuff
        if (state.contract.default_account && state.contract.initializedContracts) {
            common.update_fee_info();
        }
    });
}

async function init(init = true, name, walletlink = false) {
    console.time('initswap')

    let default_account;
    state.contract.initializedContracts = false;

    try {
        // If injected provider available, try auto-connecting
        // Should only work with Coinbase Wallet, not MetaMask
        if (window.ethereum?.isCoinbaseWallet && config.blocknativeSupport === true) {
            const connectedAddresses = await window.ethereum.request({ method: 'eth_requestAccounts' });
            if (connectedAddresses.length > 0) {
                const alreadyConnectedWallets = onboard.state.get().wallets;
                const hasAlreadyConnectedWallets = alreadyConnectedWallets.length > 0;

                if (!hasAlreadyConnectedWallets) {
                    await onboard.connectWallet({
                        autoSelect: { label: 'Coinbase', disableModals: true },
                    });
                    default_account = connectedAddresses[0];
                } else {
                    // Prevent active state.contract.web3 provider from being overriden by
                    // reinitialization at beginning of code block
                    const lastConnectedWallet = alreadyConnectedWallets[0];
                    state.contract.web3 = new Web3(lastConnectedWallet.provider);
                }
            }
        }

        if (typeof default_account === 'undefined') {
            if (config.blocknativeSupport === true) {
                state.contract.web3 = new Web3(infura_url);

                const savedSelectedWallet = localStorage.getItem('selectedWallet');

                if (savedSelectedWallet) {
                    const alreadyConnectedWallets = onboard.state.get().wallets;
                    const hasAlreadyConnectedWallets = alreadyConnectedWallets.length > 0;

                    if (!hasAlreadyConnectedWallets) {
                        await onboard.connectWallet({
                            autoSelect: { label: savedSelectedWallet, disableModals: true },
                        });
                    } else {
                        // Prevent active state.contract.web3 provider from being overriden by
                        // reinitialization at beginning of code block
                        const lastConnectedWallet = alreadyConnectedWallets[0];
                        state.contract.web3 = new Web3(lastConnectedWallet.provider);
                    }
                }

                state.contract.multicall = new state.contract.web3.eth.Contract(multicall_abi, multicall_address)
                default_account = (await state.contract.web3.eth.getAccounts())[0];
            } else {
                let selectedNetwork;
                let provider = new Web3.providers.HttpProvider(config.rpcUrl);
                state.contract.web3 = new Web3(provider);
                let ethereum = window.ethereum;
                if (ethereum) {
                    await ethereum.enable();
                    provider = new ethers.providers.Web3Provider(ethereum);

                    const signer = provider.getSigner();
                    state.contract.signer = signer
                    selectedNetwork = await provider.getNetwork();
                }

                state.contract.provider = provider

                // Check if the right network is selected
                const chainId = selectedNetwork?.chainId;
                if (chainId && chainId !== config.networkId) {
                    state.contract.error = `Error: wrong network type. Please switch to ${config.name}`;
                    state.contract.networkIdError = `Error: wrong network type. Please switch to ${config.name}`;
                } else {
                    state.contract.error = '';
                    state.contract.networkIdError = false;
                    state.contract.walletName = 'MetaMask';
                    state.contract.multicall = new state.contract.web3.eth.Contract(multicall_abi, multicall_address)

                    if (ethereum) {
                        default_account = (await ethereum.request({ method: 'eth_accounts' }))[0]
                    } else {
                        default_account = '';
                    }
                }

                // Check whether the network is functioning properly
                if (!state.contract.networkIdError) {
                    try {
                        const lastBlockNumber = await provider.getBlockNumber();
                        const { timestamp: lastBlockTimestamp } = await provider.getBlock(lastBlockNumber);

                        // Block time is usually b/w 2 and 20s
                        const isLastBlockDelayed = (getNowTimestamp() - lastBlockTimestamp) > 120;

                        state.contract.networkDelayError = isLastBlockDelayed && config.blockchainId !== 'optimism' ?
                            `The ${config.name} network seems to be having issues at the moment, please check back again soon.` :
                            '';

                    } catch (err) { }
                }
            }
        }

        state.contract.default_account = default_account;
        if (init) await state.init(name);
        state.contract.initializedContracts = true;
        console.timeEnd('initswap')
    }
    catch (err) {
        console.error(err)
    }
}

export async function changeWallets() {
    state.contract.default_account = '';

    if (config.blocknativeSupport === true) {
        const [firstWallet] = onboard.state.get().wallets;
        if (firstWallet) await onboard.disconnectWallet({ label: firstWallet.label });
    } else {
        init();
    }

    localStorage.removeItem('selectedWallet');
    state.contract.totalShare = 0;

    if (config.blocknativeSupport === true) {
        await onboard.connectWallet();
    }
}

// Detect if wallet_addEthereumChain method is available on wallet provider
export async function canAutomaticallyChangeNetwork() {
    const ERROR_CODE_METHOD_DOESNT_EXIST = -32601;

    try {
        await window.ethereum.request({
            method: 'wallet_addEthereumChain',
            params: [],
        });
    } catch (err) {
        if (err.code === ERROR_CODE_METHOD_DOESNT_EXIST) return false;
    }

    return true;
}

export async function changeNetwork() {
    const returnValue = await window.ethereum.request({
        method: 'wallet_addEthereumChain',
        params: [{
            chainId: `0x${config.networkId.toString(16)}`,
            chainName: config.name,
            nativeCurrency: {
                name: config.nativeCurrency.symbol,
                symbol: config.nativeCurrency.symbol,
                decimals: config.nativeCurrency.decimals,
            },
            rpcUrls: [config.publicRpcUrl || config.rpcUrl],
            blockExplorerUrls: [config.explorerBaseUrl.root],
        }],
    });

    const isSuccess = returnValue === null;
    if (isSuccess) window.location.reload();
}

export default init;
